import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import React, { useState } from 'react';
import { OrganizationImage } from '../OrganizationImage';
import { ErrorMessage } from './styles/ErrorMessage';
import { InputBlockWrapper } from './styles/InputBlockWrapper';
import { InputWrapper } from './styles/InputWrapper';
import { LeftSide } from './styles/LeftSide';
import { RightSide } from './styles/RightSide';
import { RightSideColumns } from './styles/RightSideColumns';
import { Wrapper } from './styles/Wrapper';

type OrganizationNameForm = {
  name: string;
  slug: string;
};

export const NameSection = () => {
  const { call } = useAPI();
  const [error, setError] = useState<string | null>();

  const { updateMe, organization } = useAuthContext();

  const [form, setForm] = useState<OrganizationNameForm>({
    name: organization?.name ?? '',
    slug: organization?.slug ?? '',
  });

  const updateOrganizationMutation = useMutation({
    mutationKey: organizationsKeys.detail(organization?.id ?? ''),
    mutationFn: ({ organizationId, values }: { organizationId: string; values: OrganizationNameForm }) =>
      call(
        () =>
          OrganizationsService.update({
            oid: organizationId,
            requestBody: {
              name: values.name,
              slug: values.slug,
            },
          }),
        { onError: (message) => setError(message || 'Unable to save the organization.') },
      ),
    onMutate: () => setError(null),
    onSuccess: async (_data, { values }) => {
      if (organization) {
        organization.name = values.name;
        organization.slug = values.slug;
      }
      await updateMe();
    },
  });
  const loading = updateOrganizationMutation.isPending;

  const submit = () => {
    if (!organization) {
      return;
    }
    updateOrganizationMutation.mutate({ organizationId: organization.id, values: form });
  };

  return (
    <div>
      <Wrapper>
        <LeftSide>
          <h3>General</h3>
          <p>Update the icon and name for your organization.</p>
        </LeftSide>

        <RightSide>
          <OrganizationImage />
          <RightSideColumns>
            <InputBlockWrapper>
              <InputWrapper>
                <label htmlFor="name">Organization name</label>
                <Input
                  className="form-input w-full"
                  disabled={loading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  name="name"
                  value={form.name}
                  label="Organization name"
                />
              </InputWrapper>

              <InputWrapper>
                <label htmlFor="slug">Organization URL</label>
                <Input
                  className="form-input w-full"
                  disabled={true}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      slug: e.target.value,
                    })
                  }
                  name="slug"
                  value={form.slug}
                  label="Organization URL"
                />
              </InputWrapper>
            </InputBlockWrapper>
          </RightSideColumns>
        </RightSide>
      </Wrapper>
      <div className="grid place-items-end max-w-[405px]">
        <div className="w-36">
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <BoldButton className="my-2 w-[140px]" disabled={loading || !form.name} onClick={submit}>
            Save
          </BoldButton>
        </div>
      </div>
    </div>
  );
};
